import { useRef, useState } from 'react';
import moment from 'moment-timezone';
import { Dialog } from '@headlessui/react';
import { Button } from 'components/Button';
import { Alert } from 'components/Alert';
import { Modal } from 'components/Modal';
import { ManagementSelectFilter } from 'components/ManagementFilters';
import {
  ManagementCard,
  ManagementCardList,
  Pill,
} from 'components/ManagementCard';
import { DataTable, DataTableMessageRow } from 'components/DataTable';
import { pageCardClasses } from 'components/AppLayout/styles';
import { useSendHistory, useSendHistoryDetail } from 'hooks/useSendHistory';
import { toErrorMessage } from 'hooks/utils';
import {
  sendHistoryStrings as strings,
  summaryManagementStrings as summary,
  commonStrings,
  strings as sharedStrings,
} from 'constants/strings';
import type { TSummarySourceType } from 'types/summary';
import type { TSendHistoryRow } from 'types/sendHistory';
import LegacySummaryManagement from '../SummaryManagement';
import { ClickedUsersModal } from '../SummaryManagement/ClickedUsersModal';
import {
  summaryStatusTone,
  formatSummaryCount,
  resolveUnavailableRecipientBadge,
  resolveUnknownRecipientBadge,
} from '../SummaryManagement/summaryDisplay';
import { SendHistoryDetail } from './SendHistoryDetail';
import { formatSendHistoryDate } from './format';

const SOURCE_OPTIONS = (
  ['BULK', 'CHAT_NOTIFICATION', 'AUTO_REPLY', 'GREETING', 'TEST'] as const
).map((source) => ({ value: source, label: summary.sourceTypes[source] }));

export default function SendHistory() {
  const [archive, setArchive] = useState(false);
  return archive ? (
    <LegacySummaryManagement
      initialMode="LEGACY_OPEN"
      onBack={() => setArchive(false)}
    />
  ) : (
    <HistoryList onArchive={() => setArchive(true)} />
  );
}

function HistoryList({ onArchive }: { onArchive: () => void }) {
  const currentYear = moment().tz('Asia/Tokyo').year();
  const [yearInput, setYearInput] = useState(String(currentYear));
  const [year, setYear] = useState(currentYear);
  const [sourceType, setSourceType] = useState<TSummarySourceType | ''>('');
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [clickedOpen, setClickedOpen] = useState(false);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const returnFocusRef = useRef<HTMLButtonElement | null>(null);
  const detailTriggerRef = useRef<HTMLButtonElement | null>(null);
  const list = useSendHistory({
    from: `${year}-01-01`,
    to: `${year}-12-31`,
    currentPage: page,
    pageSize: 10,
    ...(sourceType ? { sourceType } : {}),
  });
  const detail = useSendHistoryDetail(selectedId);
  const rows = list.data?.data ?? [];
  // サーバの文言があればそれを出し、無ければ既定文言に落とす（useSummaryと同じ扱い）。
  const listError = list.isError
    ? toErrorMessage(list.error, summary.errors.loadFailed)
    : null;
  const detailError = detail.isError
    ? toErrorMessage(detail.error, strings.detailLoadFailed)
    : null;
  const reset = () => {
    returnFocusRef.current = null;
    setSelectedId(null);
    setClickedOpen(false);
  };
  const openDetail = (id: string, trigger: HTMLButtonElement) => {
    detailTriggerRef.current = trigger;
    setSelectedId(id);
  };
  const reload = () => {
    void list.refetch();
    if (selectedId) void detail.refetch();
  };
  const search = (event: React.FormEvent) => {
    event.preventDefault();
    const value = Number(yearInput);
    const nextYear =
      Number.isInteger(value) && value >= 2000 && value <= currentYear
        ? value
        : currentYear;
    setYearInput(String(nextYear));
    setYear(nextYear);
    setPage(1);
    reset();
    if (nextYear === year && page === 1) void list.refetch();
  };
  const panel = detail.isPending ? (
    <p role="status" className="py-8 text-center text-sm text-muted">
      {commonStrings.loading}
    </p>
  ) : detailError ? (
    <Alert>{detailError}</Alert>
  ) : detail.data ? (
    <SendHistoryDetail
      key={selectedId}
      detail={detail.data}
      onClickedUsers={(event) => {
        returnFocusRef.current = event.currentTarget;
        setClickedOpen(true);
      }}
    />
  ) : null;
  const emptyMessage = list.isPending
    ? commonStrings.loading
    : (listError ?? strings.empty);
  return (
    <>
      <section className={`flex w-full min-w-0 flex-col gap-5 ${pageCardClasses.regular}`}>
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-ink">{strings.title}</h1>
            <p className="mt-2 text-xs text-muted">{strings.description}</p>
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={reload}
            disabled={list.isFetching}
          >
            {summary.search.reloadButton}
          </Button>
        </header>
        <form className="flex flex-wrap items-end gap-3" onSubmit={search}>
          <label className="grid gap-1 text-xs text-muted">
            {summary.search.deliveryYearLabel}
            <input
              aria-label={summary.search.deliveryYearLabel}
              type="number"
              min={2000}
              max={currentYear}
              className="w-28 rounded-xl border-border text-sm text-ink"
              value={yearInput}
              onChange={(event) => setYearInput(event.target.value)}
            />
          </label>
          <ManagementSelectFilter
            label={summary.search.sourceTypeLabel}
            value={sourceType}
            onChange={(value) => {
              setSourceType(value as TSummarySourceType | '');
              setPage(1);
              reset();
            }}
            onClear={() => {
              setSourceType('');
              setPage(1);
              reset();
            }}
            options={SOURCE_OPTIONS}
          />
          <Button type="submit">{commonStrings.search}</Button>
          {list.data?.hasLegacyArchive && (
            <Button type="button" variant="outline" onClick={onArchive}>
              {summary.archive.open}
            </Button>
          )}
        </form>
        <p className="text-xs text-muted">{strings.filterHint}</p>
        {listError && rows.length > 0 && <Alert>{listError}</Alert>}
        <div className="min-w-0">
          <div className="table-scroll hidden overflow-x-auto rounded-2xl border border-border/70 xl:block">
            <DataTable
              minWidthClassName="min-w-[720px]"
              headerRow={
                <tr>
                  <th scope="col" className="w-40 px-3 py-3 text-xs">
                    {summary.table.startedAt}
                  </th>
                  <th scope="col" className="w-28 px-3 py-3 text-xs">
                    {summary.table.sourceType}
                  </th>
                  <th scope="col" className="px-3 py-3 text-xs">
                    {strings.content}
                  </th>
                  <th scope="col" className="w-28 px-3 py-3 text-xs">
                    {summary.table.status}
                  </th>
                  <th scope="col" className="w-24 px-3 py-3 text-right text-xs">
                    {summary.table.sentRecipientCount}
                  </th>
                  <th scope="col" className="w-24 px-3 py-3 text-right text-xs">
                    {summary.table.detail}
                  </th>
                </tr>
              }
            >
              {rows.length ? (
                rows.map((row) => (
                  <tr
                    key={row.id}
                    className="border-t border-border/60 text-sm transition-colors hover:bg-surface/60"
                  >
                    <td className="px-3 py-4 text-xs leading-5 tabular-nums">
                      {formatSendHistoryDate(row.startedAt)}
                    </td>
                    <td className="px-3 py-3">
                      {summary.sourceTypes[row.sourceType]}
                      {row.sourceLabel && (
                        <p
                          className="mt-1 line-clamp-2 break-words text-xs text-muted"
                          title={row.sourceLabel}
                        >
                          {row.sourceLabel}
                        </p>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      <p
                        className="line-clamp-2 break-words leading-6"
                        title={row.contentSummary ?? undefined}
                      >
                        {row.contentSummary ?? strings.unavailable}
                      </p>
                    </td>
                    <td className="px-3 py-3">
                      <Status row={row} />
                    </td>
                    <td
                      className="px-3 py-3 text-right tabular-nums"
                      title={row.incompleteReason ?? strings.sentHint}
                    >
                      {formatSummaryCount(row.sentRecipientCount)}
                    </td>
                    <td className="px-3 py-3 text-right">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        aria-haspopup="dialog"
                        onClick={(event) =>
                          openDetail(row.id, event.currentTarget)
                        }
                      >
                        {summary.table.detail}
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <DataTableMessageRow colSpan={6} message={emptyMessage} />
              )}
            </DataTable>
          </div>
          <div className="xl:hidden">
            <ManagementCardList
              className="md:grid-cols-2"
              loading={list.isPending}
              isEmpty={!rows.length}
              loadingMessage={commonStrings.loading}
              emptyMessage={emptyMessage}
            >
              {rows.map((row) => (
                <li key={row.id}>
                  <ManagementCard title={formatSendHistoryDate(row.startedAt)}>
                    <div className="flex flex-wrap items-center gap-2">
                      <Pill>{summary.sourceTypes[row.sourceType]}</Pill>
                      <Status row={row} />
                    </div>
                    <p className="line-clamp-2 break-words text-sm">
                      {row.contentSummary ?? strings.unavailable}
                    </p>
                    <p className="mt-2 text-xs text-muted">
                      {summary.table.sentRecipientCount}:{' '}
                      {formatSummaryCount(row.sentRecipientCount)}
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="mt-2 justify-self-start"
                      aria-haspopup="dialog"
                      onClick={(event) =>
                        openDetail(row.id, event.currentTarget)
                      }
                    >
                      {summary.table.detail}
                    </Button>
                  </ManagementCard>
                </li>
              ))}
            </ManagementCardList>
          </div>
          <div className="mt-4 flex items-center justify-between gap-3 text-sm">
            <span>
              {list.data?.total ?? 0}
              {commonStrings.countSuffix} ·{' '}
              {list.data?.totalPage
                ? `${page} / ${list.data.totalPage}`
                : '0 / 0'}
            </span>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                disabled={page <= 1 || list.isFetching}
                onClick={() => {
                  setPage(page - 1);
                  reset();
                }}
              >
                {sharedStrings.common.pagination.previous}
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={
                  page >= (list.data?.totalPage ?? 0) || list.isFetching
                }
                onClick={() => {
                  setPage(page + 1);
                  reset();
                }}
              >
                {sharedStrings.common.pagination.next}
              </Button>
            </div>
          </div>
        </div>
        <Modal
          open={Boolean(selectedId)}
          onClose={reset}
          onAfterClose={() => {
            // 背景のinert解除がブラウザーに反映されてから一覧へ戻す。
            requestAnimationFrame(() => {
              requestAnimationFrame(() =>
                detailTriggerRef.current?.focus({ preventScroll: true }),
              );
            });
          }}
          size="xl"
          initialFocus={
            returnFocusRef.current ? returnFocusRef : closeButtonRef
          }
          mobileFullScreen
        >
          <div className="flex h-[100dvh] min-h-0 flex-col p-4 sm:h-auto sm:max-h-[calc(100dvh-5rem)] sm:p-0">
            <div className="flex shrink-0 items-center justify-between gap-3 border-b border-border/70 pb-4">
              <Dialog.Title className="text-lg font-semibold text-ink">
                {strings.detailTitle}
              </Dialog.Title>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => void detail.refetch()}
                  disabled={detail.isFetching}
                >
                  {summary.search.reloadButton}
                </Button>
                <Button
                  ref={closeButtonRef}
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={reset}
                >
                  {commonStrings.close}
                </Button>
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pt-4 sm:pr-2">
              {panel}
            </div>
          </div>
          <ClickedUsersModal
            dispatchId={selectedId}
            open={clickedOpen}
            onClose={() => setClickedOpen(false)}
            history
          />
        </Modal>
      </section>
    </>
  );
}

function Status({ row }: { row: TSendHistoryRow }) {
  const badges = [
    resolveUnavailableRecipientBadge(row),
    resolveUnknownRecipientBadge(row),
  ].filter((badge): badge is string => Boolean(badge));
  return (
    <div className="flex flex-wrap gap-1">
      <Pill tone={summaryStatusTone[row.status]}>
        {summary.deliveryStatus[row.status]}
      </Pill>
      {badges.map((badge) => (
        <Pill key={badge} tone="warning">
          {badge}
        </Pill>
      ))}
    </div>
  );
}
